import { paths, safeAppPath, isUlid } from './paths';

/*
 * Course Builder notifications in the bell (CONTRACTS.md, "Notifications").
 * They share the social notifications table, so the bell sees both kinds.
 */
export const AI_COURSE_NOTIFICATIONS = {
  ai_course_ready: { icon: '🎓', key: 'ready', text: 'Your course “{{title}}” is ready to review' },
  ai_course_lesson_updated: { icon: '✨', key: 'lessonUpdated', text: 'Your AI agent updated a lesson in “{{title}}”' },
  ai_course_needs_input: { icon: '✋', key: 'needsInput', text: '“{{title}}” needs your input to continue' },
  ai_course_build_failed: { icon: '⚠️', key: 'buildFailed', text: 'The build of “{{title}}” stopped' },
  ai_course_shared: { icon: '🤝', key: 'shared', text: '{{actor}} shared “{{title}}” with you' },
  ai_course_review_approved: { icon: '✅', key: 'reviewApproved', text: '“{{title}}” was approved' },
  ai_course_review_changes: { icon: '📝', key: 'reviewChanges', text: 'Changes requested on “{{title}}”' },
  ai_course_review_rejected: { icon: '⛔', key: 'reviewRejected', text: '“{{title}}” was not approved' },
  ai_course_published: { icon: '🚀', key: 'published', text: '“{{title}}” is published' },
  ai_course_reported: { icon: '🚩', key: 'reported', text: '“{{title}}” was reported' },
  ai_course_rating: { icon: '⭐', key: 'rating', text: '{{actor}} rated “{{title}}”' },
};

export function isAiCourseNotification(n) {
  return !!n && typeof n.type === 'string' && Object.prototype.hasOwnProperty.call(AI_COURSE_NOTIFICATIONS, n.type);
}

export function aiNotificationIcon(n) {
  return AI_COURSE_NOTIFICATIONS[n?.type]?.icon || '🎓';
}

/* `t` is react-i18next's; the defaults keep the bell readable before any translation exists. */
export function aiNotificationText(n, t) {
  const def = AI_COURSE_NOTIFICATIONS[n?.type];
  const vars = {
    title: String(n?.course_title || 'your course').slice(0, 120),
    actor: String(n?.actor_name || 'Someone').slice(0, 80),
  };
  if (!def) return vars.title;
  if (typeof t === 'function') return t(`courseBuilder.notif.${def.key}`, def.text, vars);
  return def.text.replace('{{title}}', vars.title).replace('{{actor}}', vars.actor);
}

/* Where clicking it goes: the server's in-app path, else the course page. */
export function aiNotificationPath(n) {
  const p = safeAppPath(n?.path);
  if (p) return p;
  if (isUlid(n?.course_id)) {
    if (n.type === 'ai_course_needs_input' || n.type === 'ai_course_build_failed') return paths.build(n.course_id);
    if (['ai_course_ready', 'ai_course_lesson_updated', 'ai_course_review_changes'].includes(n.type)) return paths.edit(n.course_id);
    return paths.course(n.course_id);
  }
  return paths.mine();
}

/* Ask the bell to look for new notifications now (a rewrite just landed). */
export const NOTIFICATIONS_REFRESH = 'lxops:notifications-refresh';
export function refreshNotifications(delayMs = 0) {
  const fire = () => { try { window.dispatchEvent(new Event(NOTIFICATIONS_REFRESH)); } catch { /* no window */ } };
  if (delayMs > 0) setTimeout(fire, delayMs); else fire();
}
// The cloud writes a build's notification just after it marks the build done.
export const NOTIFY_LAG_MS = 2000;

/*
 * What the bell shows a member: course notifications always; the social
 * ones (like/comment/reply) only on a paid plan, as before.
 */
export function visibleNotifications(list, isPaid) {
  return (Array.isArray(list) ? list : []).filter((n) => isAiCourseNotification(n) || isPaid);
}
